import { calculateAging, formatDate, formatTimeWithZoneAbbreviation } from ".";
import { DATE_FORMAT } from "../constants/app-constant";
import {
  UM_API_STATUS,
  UM_DISPLAY_STATUS,
  UM_RESPONSE_STATUS,
} from "../constants/um-constants";
import type {
  UmClinicalIndicator,
  UmEvidence,
  UmQueriesListResponse,
  UmQuery,
  UmQueryListRow,
  UmQueryOption,
  UmQueryResponse,
} from "../types/um-queries";

const CLINICAL_SECTION_LABELS: Record<string, string> = {
  SSV: "Signs and Symptoms/ Vitals",
  LIF: "Lab/Imaging Findings",
  TM: "Treatment/Management",
};

export const mapUmStatusToDisplay = (status: string): string =>
  UM_DISPLAY_STATUS[status as keyof typeof UM_DISPLAY_STATUS] ?? status;

export const isUmQueryCompleted = (
  status: string,
  response?: UmQueryResponse | null
): boolean =>
  status === UM_API_STATUS.COMPLETED ||
  response?.status === UM_RESPONSE_STATUS.COMPLETED;

export const shouldUmQueryPreviewMode = (
  query: UmQuery,
  isEdit: boolean
): boolean => {
  if (isEdit) return false;
  if (isUmQueryCompleted(query.status, query.response)) return true;
  return query.response?.status === UM_RESPONSE_STATUS.IN_PROGRESS;
};

/** API may return { queries, ... } or { data: { queries, ... } } after decrypt */
export const extractUmQueriesListResponse = (
  payload: unknown
): UmQueriesListResponse => {
  const empty: UmQueriesListResponse = { success: false, count: 0, queries: [] };

  if (!payload || typeof payload !== "object") {
    return empty;
  }

  const root = payload as Record<string, unknown>;

  if (Array.isArray(root.queries)) {
    return {
      success: Boolean(root.success),
      count: Number(root.count ?? root.queries.length),
      queries: root.queries as UmQuery[],
    };
  }

  if (root.data && typeof root.data === "object") {
    const nested = root.data as Record<string, unknown>;
    if (Array.isArray(nested.queries)) {
      return {
        success: Boolean(nested.success),
        count: Number(nested.count ?? nested.queries.length),
        queries: nested.queries as UmQuery[],
      };
    }
  }

  return empty;
};

export const extractUmQueryDetail = (payload: unknown): UmQuery | null => {
  if (!payload || typeof payload !== "object") {
    return null;
  }

  const root = payload as Record<string, unknown>;

  if (root.query && typeof root.query === "object") {
    return root.query as UmQuery;
  }

  if (root.data && typeof root.data === "object") {
    const nested = root.data as Record<string, unknown>;
    if (nested.query && typeof nested.query === "object") {
      return nested.query as UmQuery;
    }
  }

  return null;
};

export const transformUmQueriesToRows = (
  response: UmQueriesListResponse,
  timezoneName?: string
): UmQueryListRow[] => {
  return (response?.queries ?? []).map((query) => {
    const dateSource = query.sentAt || query.createdAt;
    const completed = isUmQueryCompleted(query.status, query.response);

    return {
      id: query.id,
      queryId: query.queryNumber || query.queryId,
      patientName:
        query.case?.patientName ||
        query.request?.patient?.name ||
        query.case?.name ||
        "-",
      patientMRN: query.case?.mrn || query.request?.patient?.mrn || "-",
      status: mapUmStatusToDisplay(query.status),
      statusApi: query.status,
      ageing: completed ? "-" : calculateAging(dateSource),
      queryDate: formatDate(dateSource, DATE_FORMAT.MM_DD_YYYY, timezoneName),
      createdFormatTime: formatTimeWithZoneAbbreviation(
        dateSource,
        timezoneName
      ),
    };
  });
};

export const buildUmClinicalSections = (
  indicators: UmClinicalIndicator[] = []
) => {
  const sectionData: Record<
    string,
    { title: string; items: { label: string; date: string; value: string }[] }
  > = {};

  indicators.forEach((indicator) => {
    const sectionCode = indicator.type || "UNKNOWN";
    const title = CLINICAL_SECTION_LABELS[sectionCode] || "Clinical Indicators";

    if (!sectionData[sectionCode]) {
      sectionData[sectionCode] = { title, items: [] };
    }

    sectionData[sectionCode].items.push({
      label: indicator.name,
      date: indicator.date,
      value: indicator.result || "",
    });
  });

  return Object.keys(CLINICAL_SECTION_LABELS)
    .filter((code) => sectionData[code])
    .map((code) => sectionData[code]);
};

export const buildUmEvidenceNotes = (evidences: UmEvidence[] = []): string[] =>
  evidences.map((evidence) => {
    const highlightText =
      evidence.highlights && evidence.highlights.length > 0
        ? evidence.highlights
            .map((highlight) => `<strong>${highlight}</strong>`)
            .join(", ")
        : "";

    const pageSuffix = evidence.page ? ` (Page ${evidence.page})` : "";
    const baseText = `${evidence.text}${pageSuffix}`;

    return highlightText ? `${baseText} — ${highlightText}` : baseText;
  });

export const isUmOtherOption = (option: UmQueryOption): boolean =>
  /\bother\b/i.test(option.label.trim());

/** Split section1 to highlight text after "for" through the first "." (UM query UI). */
export const splitSection1ForHighlight = (
  section1: string
): { before: string; highlight: string; after: string } | null => {
  const forMatch = section1.match(/\bfor\b/i);
  if (!forMatch || forMatch.index === undefined) return null;

  const afterForWord = forMatch.index + forMatch[0].length;
  let highlightStart = afterForWord;
  while (highlightStart < section1.length && section1[highlightStart] === " ") {
    highlightStart++;
  }

  const dotIndex = section1.indexOf(".", highlightStart);
  if (dotIndex === -1) return null;

  return {
    before: section1.slice(0, highlightStart),
    highlight: section1.slice(highlightStart, dotIndex + 1),
    after: section1.slice(dotIndex + 1),
  };
};

export const extractUmResponseSelections = (
  response: UmQueryResponse | null | undefined,
  queryStatus?: string
): { selectedIds: string[]; otherText: string } => {
  if (!response) {
    return { selectedIds: [], otherText: "" };
  }

  const hasSubmittedAnswers =
    response.status === UM_RESPONSE_STATUS.IN_PROGRESS ||
    response.status === UM_RESPONSE_STATUS.COMPLETED ||
    queryStatus === UM_API_STATUS.COMPLETED;

  if (!hasSubmittedAnswers || !response.selectedOptions?.length) {
    return { selectedIds: [], otherText: response.otherJustification ?? "" };
  }

  const selectedIds = response.selectedOptions.map((option) => option.id);
  const otherOption = response.selectedOptions.find(isUmOtherOption);
  const otherText =
    response.otherJustification ||
    (otherOption
      ? (() => {
          const suffix = otherOption.label.match(/:\s+(.+)$/);
          return suffix ? suffix[1].trim() : "";
        })()
      : "");

  return { selectedIds, otherText };
};

export const buildUmSubmitOptions = (
  questionOptions: UmQueryOption[],
  selectedIds: string[],
  otherText: string
): UmQueryOption[] =>
  selectedIds.map((id) => {
    const option = questionOptions.find((item) => item.id === id);
    if (!option) {
      return { id, label: id };
    }

    if (isUmOtherOption(option) && otherText.trim()) {
      return { id: option.id, label: `${option.label}: ${otherText.trim()}` };
    }

    return { id: option.id, label: option.label };
  });

const ALL_UM_STATUS_VALUES = [UM_API_STATUS.OPEN, UM_API_STATUS.COMPLETED];

export const filterUmQueriesByStatus = (
  rows: UmQueryListRow[],
  statusFilter: string[]
): UmQueryListRow[] => {
  if (
    statusFilter.length === 0 ||
    statusFilter.length >= ALL_UM_STATUS_VALUES.length
  ) {
    return rows;
  }

  return rows.filter((row) => statusFilter.includes(row.statusApi));
};

export const filterUmQueriesBySearch = (
  rows: UmQueryListRow[],
  search: string
): UmQueryListRow[] => {
  const term = search.trim().toLowerCase();
  if (!term) return rows;

  return rows.filter(
    (row) =>
      row.patientName.toLowerCase().includes(term) ||
      row.patientMRN.toLowerCase().includes(term) ||
      row.queryId.toLowerCase().includes(term)
  );
};

export const getUmQueryPatientInfo = (query: UmQuery) => ({
  patientName:
    query.case?.patientName ||
    query.request?.patient?.name ||
    query.case?.name ||
    "-",
  mrn: query.case?.mrn || query.request?.patient?.mrn || "-",
  dob: query.case?.dob || query.request?.patient?.dob || "-",
  gender: query.case?.gender || query.request?.patient?.gender || "-",
  admissionDate:
    query.case?.dateOfAdmission || query.request?.patient?.dos || "-",
  dischargeDate: query.case?.dateOfDischarge || "-",
});
